import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { io } from "socket.io-client";
import { QRCodeSVG } from "qrcode.react";
import Navbar from "../components/Navbar";
import BackBar from "../components/Ticketing/BackBar";
import { apiGetTicket, apiRequestTicketRefund } from "../api/ticketingApi";
import { fmtDateTime, fmtMoney, fmtTime, formatSerial } from "../components/Ticketing/format";
import { loadTicketSnapshot, saveTicketSnapshot } from "../utils/ticketCache";
import { getPersistedAuthToken } from "../utils/tokenUtils";
import { formatLiveCode } from "../utils/liveTicket";
import useLiveTicket from "../hooks/useLiveTicket";

// "23:44:17" from the server-corrected time the QR is drawn for.
const clockText = (ms) => {
  const d = new Date(ms);
  const two = (n) => String(n).padStart(2, "0");
  return `${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}`;
};

const TicketQRPage = () => {
  const { ticketId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundDone, setRefundDone] = useState(false);
  const [refundErr, setRefundErr] = useState("");
  // Non-null when what is on screen came out of localStorage, not the network.
  const [cachedAt, setCachedAt] = useState(null);

  // The showing room is only knowable after the ticket loads, which is normally
  // after the socket has already connected — so the id is carried in a ref and
  // the join is re-issued when it arrives.
  const socketRef = useRef(null);
  const showingIdRef = useRef(null);

  useEffect(() => {
    let alive = true;
    let live = false;

    // Paint the cached copy first. On a saturated venue network this is the
    // difference between a ticket that opens and "We couldn't load this
    // ticket." while the customer is standing at the door.
    const cached = loadTicketSnapshot(ticketId);
    if (cached) {
      setData(cached.data);
      setCachedAt(cached.savedAt);
      setLoading(false);
    }

    const load = () =>
      apiGetTicket(ticketId)
        .then((d) => {
          if (!alive) return;
          live = true;
          setData(d);
          setCachedAt(null);
          setError("");
          setLoading(false);
          saveTicketSnapshot(ticketId, d);
        })
        .catch(() => {
          if (!alive || live) return;
          const fallback = loadTicketSnapshot(ticketId);
          if (fallback) {
            setData(fallback.data);
            setCachedAt(fallback.savedAt);
          } else {
            setError("We couldn't load this ticket.");
          }
          setLoading(false);
        });
    load();

    // Live status, two layers:
    //  1. Socket (instant): join this ticket's room; the gate scan pushes
    //     `ticketRedeemed` and the page flips to "Admitted" immediately.
    //  2. Poll (15s fallback): sockets can drop on flaky venue Wi-Fi — the
    //     quiet re-fetch guarantees the page still converges.
    // If someone copied the QR and used it, the real holder sees it here — not
    // for the first time at the gate.
    //
    // The handshake carries the bearer token because `ticket:<id>` sits in the
    // server's REQUIRES_AUTH set: an anonymous socket is refused the join, so
    // this page has been subscribing to nothing.
    const token = getPersistedAuthToken();
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      transports: ["websocket", "polling"],
      auth: token ? { token } : undefined,
    });
    socketRef.current = socket;
    const joinRooms = () => {
      socket.emit("join", `ticket:${ticketId}`);
      if (showingIdRef.current) socket.emit("join", `showing:${showingIdRef.current}`);
    };
    socket.on("connect", joinRooms);
    socket.on("reconnect", joinRooms);
    socket.on("ticketRedeemed", (p) => {
      if (!alive || String(p?.ticketId) !== String(ticketId)) return;
      setData((prev) => {
        if (!prev?.ticket) return prev;
        // A RE_ENTRY ticket stays VALID by design — that is what lets the
        // holder back in — so only a single-use ticket becomes USED.
        const reEntry = prev.ticket.entryPolicy && prev.ticket.entryPolicy !== "SINGLE_USE";
        return {
          ...prev,
          ticket: {
            ...prev.ticket,
            status: reEntry ? prev.ticket.status : "USED",
            displayStatus: reEntry ? "ADMITTED" : "USED",
            redeemedAt: p.redeemedAt || new Date().toISOString(),
            qrToken: reEntry ? prev.ticket.qrToken : null,
          },
        };
      });
    });
    // A refund approved while the page is open. Previously the customer learned
    // about it at the scanner.
    socket.on("ticketRefunded", (p) => {
      if (!alive || String(p?.ticketId) !== String(ticketId)) return;
      setData((prev) =>
        prev?.ticket
          ? {
              ...prev,
              ticket: { ...prev.ticket, status: "REFUNDED", displayStatus: "REFUNDED", qrToken: null },
            }
          : prev
      );
    });
    // The showing itself was cancelled. `cancelShowing` refunds every still-VALID
    // ticket, but caps the per-ticket emit loop at 50 for a full house — so the
    // showing-level event is the one that reliably arrives.
    socket.on("showingCancelled", (p) => {
      if (!alive) return;
      const mine = !p?.showingId || String(p.showingId) === String(showingIdRef.current || "");
      if (!mine) return;
      const ids = Array.isArray(p?.ticketIds) ? p.ticketIds.map(String) : [];
      const refundsThisTicket = !ids.length || ids.includes(String(ticketId));
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          showing: { ...(prev.showing || {}), status: "CANCELLED" },
          ticket:
            prev.ticket && refundsThisTicket
              ? { ...prev.ticket, status: "REFUNDED", displayStatus: "REFUNDED", qrToken: null }
              : prev.ticket,
        };
      });
    });
    const iv = setInterval(() => {
      if (!alive) return;
      if (document.visibilityState === "hidden") return;
      load();
    }, 15000);
    return () => {
      alive = false;
      clearInterval(iv);
      socket.off("ticketRedeemed");
      socket.off("ticketRefunded");
      socket.off("showingCancelled");
      socket.disconnect();
      socketRef.current = null;
    };
  }, [ticketId]);

  const submitRefund = (e) => {
    e.preventDefault();
    if (refundBusy) return;
    setRefundBusy(true);
    setRefundErr("");
    apiRequestTicketRefund(ticketId, refundReason.trim())
      .then(() => setRefundDone(true))
      .catch((err) =>
        setRefundErr(err?.response?.data?.message || "We couldn't submit your refund request.")
      )
      .finally(() => setRefundBusy(false));
  };

  const t = data?.ticket;
  const showing = data?.showing;

  // The live QR (16 Oct 2026): drawn here from a per-ticket key, changing
  // every 15 seconds, so a screenshot or a forwarded copy stops admitting
  // within a minute. Older servers send no key and the static QR is drawn as
  // before.
  const live = useLiveTicket({
    ticketId,
    ticket: t,
    onMoved: (moved) =>
      setData((prev) => (prev ? { ...prev, ticket: { ...(prev.ticket || {}), ...moved } } : prev)),
  });
  const liveOn = live.mode === "live" && Boolean(live.frame);
  const showingId = showing?._id
    ? String(showing._id)
    : t?.showingId
      ? String(t.showingId)
      : null;

  useEffect(() => {
    showingIdRef.current = showingId;
    const socket = socketRef.current;
    if (socket && socket.connected && showingId) {
      socket.emit("join", `showing:${showingId}`);
    }
  }, [showingId]);

  // Render displayStatus, not status — see the maps in MyTicketsPage.
  const effectiveStatus = t?.displayStatus || t?.status || null;
  const showingCancelled = String(showing?.status || "") === "CANCELLED";
  const admitted = effectiveStatus === "ADMITTED";
  const used = effectiveStatus === "USED";
  const invalid = effectiveStatus === "VOID" || effectiveStatus === "REFUNDED";
  const scannable = (liveOn || Boolean(t?.qrToken)) && !used && !invalid && !showingCancelled;
  const showElsewhere = live.mode === "elsewhere" && !used && !invalid && !showingCancelled;
  const serialText = formatSerial(t?.serial);

  return (
    <>
      <Navbar />
      <Page>
        <BackBar label="My tickets" fallback="/tickets" />
        {loading && <Muted>Loading…</Muted>}
        {!loading && error && <Muted>{error}</Muted>}
        {!loading && showingCancelled && (
          <CancelAlert>
            <CancelTitle>This screening was cancelled</CancelTitle>
            <CancelBody>
              Do not travel to the venue. Every ticket for this showing is being
              refunded — the organizer will confirm.
            </CancelBody>
          </CancelAlert>
        )}
        {!loading && !error && t && (
          <Ticket>
            <Stub>
              <EvTitle>{data.event?.title || "Ticket"}</EvTitle>
              <Meta>{data.showing?.startsAt ? fmtDateTime(data.showing.startsAt) : ""}</Meta>
              {/* The list-seeded offline snapshot carries the venue name on the
                  event rather than a full venue document. */}
              {(data.venue?.name || data.event?.venueName) && (
                <Meta>{data.venue?.name || data.event?.venueName}</Meta>
              )}
              <SeatRow>
                {t.seatLabel && <Chip>{t.unitLabel && t.unitLabel !== "ticket" ? t.unitLabel : "Seat"} {t.seatLabel}</Chip>}
                <Chip>{t.ticketTypeName}</Chip>
                {Number(t.admissionsCount || 1) > 1 && <Chip>Admits {t.admissionsCount} guests</Chip>}
                <Chip>{fmtMoney(t.price, t.currency)}</Chip>
              </SeatRow>
            </Stub>

            {showElsewhere ? (
              <Elsewhere>
                <ElsewhereTitle>Show this ticket here</ElsewhereTitle>
                <ElsewhereBody>
                  This ticket has a live QR that shows on one device at a time,
                  usually the Lusuku app on your phone. Show it on this device
                  instead and the QR on the other device stops working.
                </ElsewhereBody>
                <MoveBtn type="button" onClick={live.moveHere} disabled={live.moving}>
                  {live.moving ? "Moving..." : "Show it on this device"}
                </MoveBtn>
                {!!live.moveError && <MoveError>{live.moveError}</MoveError>}
              </Elsewhere>
            ) : (
              <QrArea $dim={!scannable}>
                <QRCodeSVG value={(liveOn ? live.frame.token : t.qrToken) || ""} size={220} level="M" includeMargin />
              </QrArea>
            )}

            {/* The live strip: a clock that ticks and a bar that runs down,
                so the person at the door can see this is not a still image. */}
            {liveOn && scannable && (
              <LiveStrip>
                <LiveRow>
                  <LiveDot $dim={live.frame.secondsLeft % 2 === 0} />
                  <LiveText>LIVE {clockText(live.frame.nowMs)}</LiveText>
                  <LiveNext>new code in {live.frame.secondsLeft}s</LiveNext>
                </LiveRow>
                <LiveTrack>
                  <LiveBar style={{ width: `${Math.round(live.frame.fractionLeft * 100)}%` }} />
                </LiveTrack>
              </LiveStrip>
            )}

            {/* The serial, promoted from decoration to fallback. An
                admit-by-serial path is going into the backend, which makes this
                the working alternative when the QR will not scan — a cracked
                screen, a matte protector, a reader that will not focus. It has
                to be legible across a foyer and speakable in one breath. */}
            {!!serialText && !showElsewhere && (
              <SerialBlock>
                <SerialCaption>
                  {liveOn ? "If the QR won't scan, read out both" : "If the QR won't scan, read this out"}
                </SerialCaption>
                <Serial aria-label={`Ticket serial ${serialText}`}>{serialText}</Serial>
                {liveOn && scannable && (
                  <LiveCode aria-label={`Code ${live.frame.code.split("").join(" ")}`}>
                    Code {formatLiveCode(live.frame.code)}
                  </LiveCode>
                )}
              </SerialBlock>
            )}

            {cachedAt != null && (
              <OfflineNote>
                Offline copy — last updated at {fmtTime(new Date(cachedAt).toISOString())}
              </OfflineNote>
            )}

            {admitted && (
              <Banner $admitted>
                Admitted — this ticket has already been through the gate
                {t.redeemedAt ? ` (${fmtDateTime(t.redeemedAt)})` : ""}
              </Banner>
            )}
            {used && (
              <Banner $used>
                Admitted — this ticket has been used
                {t.redeemedAt ? ` (${fmtDateTime(t.redeemedAt)})` : ""}
              </Banner>
            )}
            {invalid && (
              <Banner>
                {effectiveStatus === "REFUNDED"
                  ? "Refunded — this ticket is no longer valid"
                  : "Void — this ticket is no longer valid"}
              </Banner>
            )}
            {!used && !invalid && showingCancelled && (
              <Banner>Screening cancelled — this ticket will not admit you</Banner>
            )}
            {scannable && (
              <>
                <Valid>
                  {liveOn
                    ? "Show this QR at the entrance. It changes every few seconds, so a screenshot will not work. One-time entry."
                    : "Show this QR at the entrance. One-time entry."}
                </Valid>
                <RefundArea>
                  {refundDone ? (
                    <RefundNote>Refund requested — the organizer will review it.</RefundNote>
                  ) : !refundOpen ? (
                    <RefundLink type="button" onClick={() => setRefundOpen(true)}>
                      Request a refund
                    </RefundLink>
                  ) : (
                    <RefundForm onSubmit={submitRefund}>
                      <RefundInput
                        placeholder="Why are you requesting a refund?"
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        required
                      />
                      <RefundBtn type="submit" disabled={refundBusy}>
                        {refundBusy ? "Sending…" : "Submit"}
                      </RefundBtn>
                    </RefundForm>
                  )}
                  {!refundDone && refundErr && <RefundErr>{refundErr}</RefundErr>}
                </RefundArea>
              </>
            )}
          </Ticket>
        )}
      </Page>
    </>
  );
};

const Page = styled.div`
  max-width: 420px; margin: 0 auto;
  padding: calc(var(--lusuku-nav-h, 130px) + 16px) 16px 48px; min-height: 60vh;
`;
const Muted = styled.div`color:#6b7280;padding:24px 0;text-align:center;`;
const Ticket = styled.div`
  background:#fff;border-radius:18px;box-shadow:0 8px 30px rgba(0,0,0,.10);overflow:hidden;text-align:center;
`;
const Stub = styled.div`background:#0f2e2e;color:#fff;padding:22px 20px;`;
const EvTitle = styled.div`font-size:19px;font-weight:800;`;
const Meta = styled.div`font-size:13px;opacity:.85;margin-top:4px;`;
const SeatRow = styled.div`display:flex;gap:8px;justify-content:center;flex-wrap:wrap;margin-top:12px;`;
const Chip = styled.span`background:rgba(255,255,255,.14);border-radius:20px;padding:4px 12px;font-size:12px;font-weight:600;`;
const QrArea = styled.div`
  padding:26px;display:flex;justify-content:center;
  opacity:${(p)=>(p.$dim?0.25:1)};filter:${(p)=>(p.$dim?"grayscale(1)":"none")};
`;
const SerialBlock = styled.div`
  margin:0 18px 14px;padding:12px 10px;border:1px solid #e5e7eb;border-radius:12px;
  background:#f9fafb;
`;
const SerialCaption = styled.div`
  font-size:11.5px;color:#6b7280;text-transform:uppercase;letter-spacing:.6px;
  font-weight:700;margin-bottom:6px;
`;
const Serial = styled.div`
  font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;
  font-size:22px;line-height:28px;font-weight:700;color:#111827;letter-spacing:1.5px;
`;
const OfflineNote = styled.div`
  margin:0 18px 12px;padding:8px 10px;border:1px solid #fde68a;border-radius:10px;
  background:#fffbeb;color:#b45309;font-size:12.5px;font-weight:600;
`;
const CancelAlert = styled.div`
  border:1px solid #fecaca;background:#fef2f2;border-radius:14px;padding:14px;
  margin-bottom:12px;text-align:left;
`;
const CancelTitle = styled.div`color:#b91c1c;font-weight:800;font-size:14.5px;`;
const CancelBody = styled.div`color:#991b1b;font-size:12.5px;margin-top:4px;line-height:18px;`;
const Banner = styled.div`
  background:${(p)=>(p.$used?"#eef2f7":p.$admitted?"#fffbeb":"#fef2f2")};
  color:${(p)=>(p.$used?"#475569":p.$admitted?"#b45309":"#dc2626")};
  padding:14px;font-weight:700;font-size:14px;
`;
const Valid = styled.div`padding:16px 20px 6px;color:#4d7a10;font-size:13.5px;font-weight:600;`;
const RefundArea = styled.div`padding:0 20px 20px;`;
const RefundLink = styled.button`
  background:none;border:none;padding:0;color:#6b7280;font-size:12.5px;
  text-decoration:underline;cursor:pointer;
`;
const RefundNote = styled.div`color:#6b7280;font-size:12.5px;`;
const RefundForm = styled.form`display:flex;gap:8px;justify-content:center;margin-top:4px;`;
const RefundInput = styled.input`
  flex:1;min-width:0;border:1px solid #e5e7eb;border-radius:8px;padding:8px 10px;
  font-size:13px;color:#374151;
  &::placeholder{color:#9ca3af;}
`;
const RefundBtn = styled.button`
  border:none;border-radius:8px;background:#0f2e2e;color:#fff;
  font-size:13px;font-weight:600;padding:8px 14px;cursor:pointer;
  &:disabled{opacity:.6;cursor:default;}
`;
const RefundErr = styled.div`color:#dc2626;font-size:12.5px;margin-top:8px;`;
const Elsewhere = styled.div`padding:26px 24px;`;
const ElsewhereTitle = styled.div`font-size:16px;font-weight:800;color:#111827;`;
const ElsewhereBody = styled.div`font-size:13.5px;line-height:20px;color:#6b7280;margin-top:8px;`;
const MoveBtn = styled.button`
  margin-top:16px;border:none;border-radius:10px;background:#0f2e2e;color:#fff;
  font-size:14px;font-weight:700;padding:12px 22px;cursor:pointer;
  &:disabled{opacity:.6;cursor:default;}
`;
const MoveError = styled.div`color:#dc2626;font-size:12.5px;margin-top:10px;`;
const LiveStrip = styled.div`margin:-10px 18px 14px;`;
const LiveRow = styled.div`display:flex;align-items:center;justify-content:center;gap:8px;`;
const LiveDot = styled.span`
  width:9px;height:9px;border-radius:50%;display:inline-block;
  background:${(p)=>(p.$dim?"#86efac":"#16a34a")};
`;
const LiveText = styled.span`
  font-size:13px;font-weight:800;color:#14532d;letter-spacing:.5px;font-variant-numeric:tabular-nums;
`;
const LiveNext = styled.span`font-size:12px;color:#6b7280;font-variant-numeric:tabular-nums;`;
const LiveTrack = styled.div`height:4px;border-radius:2px;background:#e5e7eb;margin-top:8px;overflow:hidden;`;
const LiveBar = styled.div`height:4px;background:#16a34a;transition:width 1s linear;`;
const LiveCode = styled.div`
  font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;
  font-size:20px;line-height:26px;font-weight:700;color:#14532d;letter-spacing:1.5px;margin-top:6px;
`;

export default TicketQRPage;
